import { useMemo } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQueries } from "@tanstack/react-query"
import { type ColumnDef } from "@tanstack/react-table"
import { BarChart3 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { EntityTablePage } from "@/features/shared/components/entity-table-page"
import { useThresholdSets } from "@/lib/hooks/use-thresholds"
import { fetchInstrumentLite } from "@/lib/api/instruments"
import { useSites } from "@/lib/hooks/use-sites"
import type { ThresholdSet, Threshold } from "@/types/api"

export const Route = createFileRoute("/_authenticated/thresholds")({
  component: ThresholdsPage,
})

const levelOrder: Record<string, number> = { green: 0, amber: 1, red: 2 }
const levelVariant: Record<string, "success" | "warning" | "destructive"> = {
  green: "success",
  amber: "warning",
  red: "destructive",
}

function sortThresholds(thresholds: Threshold[]) {
  return [...thresholds].sort((a, b) => {
    const lA = levelOrder[a.level ?? ""] ?? 3
    const lB = levelOrder[b.level ?? ""] ?? 3
    if (lA !== lB) return lA - lB
    if (a.boundary !== b.boundary) return a.boundary.localeCompare(b.boundary)
    return a.channel.localeCompare(b.channel)
  })
}

function getColumns(
  siteNameMap: Map<string, string>,
  instrumentNameMap: Map<string, string>,
): ColumnDef<ThresholdSet, unknown>[] {
  return [
    {
      accessorKey: "site",
      header: "Site",
      cell: ({ row }) => siteNameMap.get(row.original.site) ?? row.original.site,
    },
    {
      accessorKey: "measurement_id",
      header: "Device",
      cell: ({ row }) => (
        <span className="font-medium text-[var(--color-text-primary)]">
          {instrumentNameMap.get(row.original.measurement_id) ?? row.original.measurement_id}
        </span>
      ),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => (
        <span className="max-w-[300px] truncate block">
          {row.original.description || "—"}
        </span>
      ),
    },
    {
      id: "thresholds",
      header: "Thresholds",
      cell: ({ row }) => {
        const levels = [...new Set(
          row.original.thresholds
            .map((t) => t.level)
            .filter(Boolean)
            .sort((a, b) => (levelOrder[a ?? ""] ?? 3) - (levelOrder[b ?? ""] ?? 3)),
        )]
        const sorted = sortThresholds(row.original.thresholds)
        return (
          <div className="relative group">
            <div className="flex gap-1">
              {levels.map((level) => (
                <Badge key={level} variant={levelVariant[level ?? ""] ?? "secondary"}>
                  {level}
                </Badge>
              ))}
            </div>
            <div className="hidden group-hover:block absolute z-10 top-full left-0 mt-1 rounded-lg bg-[var(--color-bg-tertiary)] text-xs overflow-hidden shadow-lg min-w-[340px]">
              <div className="grid grid-cols-4 gap-x-4 px-3 py-1.5 font-semibold">
                <span>Channel</span>
                <span>Level</span>
                <span>Boundary</span>
                <span>Value</span>
              </div>
              {sorted.map((t, i) => (
                <div
                  key={t.id ?? i}
                  className={`grid grid-cols-4 gap-x-4 px-3 py-1.5 ${i % 2 === 0 ? "bg-[var(--color-bg-secondary)]" : ""}`}
                >
                  <span>{t.channel}</span>
                  <span className="font-semibold">{t.level}</span>
                  <span>{t.boundary === "lower" ? "Lower" : "Upper"}</span>
                  <span className="font-semibold">{t.value} mm</span>
                </div>
              ))}
            </div>
          </div>
        )
      },
    },
  ]
}

function ThresholdsPage() {
  const { data: thresholds, isLoading, isError, error } = useThresholdSets()
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  const uniqueSites = useMemo(() => {
    if (!thresholds) return []
    return [...new Set(thresholds.map((t) => t.site))]
  }, [thresholds])

  const instrumentQueries = useQueries({
    queries: uniqueSites.map((site) => ({
      queryKey: ["instruments-lite", site],
      queryFn: () => fetchInstrumentLite(site),
    })),
  })

  const instrumentNameMap = useMemo(() => {
    const map = new Map<string, string>()
    instrumentQueries.forEach((q) => {
      q.data?.forEach((inst) => map.set(inst.id, inst.name))
    })
    return map
  }, [instrumentQueries])

  const columns = useMemo(
    () => getColumns(siteNameMap, instrumentNameMap),
    [siteNameMap, instrumentNameMap],
  )

  return (
    <EntityTablePage
      icon={<BarChart3 className="h-5 w-5 text-[var(--color-accent-purple)]" />}
      title="Thresholds"
      description={`${thresholds?.length ?? 0} threshold sets`}
      searchPlaceholder="Search thresholds..."
      emptyMessage="No threshold sets"
      columns={columns}
      data={thresholds}
      isLoading={isLoading}
      isError={isError}
      error={error}
      dataTour="thresholds-table"
    />
  )
}
